import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';

import EntityName from '../../core/enums/EntityName';
import { selectProjectSettings } from '../../core/state/bookingProjectSettings/selectors';
import { generateEntityFetchAsyncAction, generateEntityFetchListAsyncAction } from '../../core/state/entity/generateEntityAsyncActions';
import { useBookingLayout } from './hooks';
import { BookingLayout } from './types';

import { Actions } from '../../core/state/bookings/actions';
import ProjectDetails from './project-details/ProjectDetails';
import Toolbar from './toolbar/Toolbar';
import Calendar from './calendar/Calendar';
import BookingList from './list/BookingList';
import ManageBookingInternalModal from './modals/create-internal/ManageBookingInternalModal';
import BookingFiltersModal from './modals/booking-filters/BookingFiltersModal';
import CreateBookingExternalModal from './modals/create-external/CreateBookingExternalModal';
import LogisticPlanModal from './modals/logistic-plan/LogisticPlanModal';
import SendEmailAfterBookingModal from './modals/create-internal/send-email/SendEmailAfterBookingModal';
import BookingPrintZones from './BookingPrintZones';

import './Bookings.scss';
import './BookingsPrint.scss';

const Bookings: React.FC = () => {
  const dispatch = useDispatch();
  const [isPrinting, setIsPrinting] = useState(false);
  const params = useParams<{ projectId: string }>();
  const projectId = Number(params.projectId);
  const componentRef = useRef(null);
  const promiseResolveRef: any = useRef(null);

  const onPrintHandler = useReactToPrint({
    content: () => componentRef.current,
    onBeforeGetContent: () => {
      return new Promise((resolve) => {
        promiseResolveRef.current = resolve;
        setIsPrinting(true);
      });
    },
    onAfterPrint: () => {
      promiseResolveRef.current = null;
      setIsPrinting(false);
    }
  });

  const projectSettings = useSelector(selectProjectSettings);
  const layout = useBookingLayout();
  const [isSendEmailModal, setIsSendEmailModal] = useState(false);
  const [bookingId, setBookingId] = useState('');
  const fetchProjectSettings = useMemo(() => generateEntityFetchAsyncAction(EntityName.BookingProjectSettings), []);
  const fetchBlockedSlots = useMemo(() => generateEntityFetchListAsyncAction(EntityName.BlockedSlot), []);

  useEffect(() => {
    if (isPrinting && promiseResolveRef.current) {
      promiseResolveRef.current();
    }
  }, [isPrinting]);

  useEffect(() => {
    dispatch(Actions.closeModal());
    dispatch(Actions.changeSelectedProject(projectId));
    dispatch(Actions.resetFilters());

    dispatch(fetchProjectSettings(projectId));
    dispatch(fetchBlockedSlots({ projectId }));
  }, [dispatch, fetchBlockedSlots, fetchProjectSettings, projectId]);

  const handleOpenSendEmailModal = (id: string) => {
    setBookingId(id);
    setIsSendEmailModal(true);
  };

  return (
    <div className="bookings" ref={componentRef}>
      <div className="bookings__inner">
        <ProjectDetails projectId={projectId} />
        <Toolbar handlePrint={onPrintHandler} />
        {projectSettings && (
          <>
            {layout === BookingLayout.Calendar && <Calendar isPrinting={isPrinting} />}
            {layout === BookingLayout.List && <BookingList isPrinting={isPrinting} />}
            <ManageBookingInternalModal openSendEmailModal={(id) => handleOpenSendEmailModal(id)} />
            <SendEmailAfterBookingModal isOpen={isSendEmailModal} closeModal={() => setIsSendEmailModal(false)} bookingId={bookingId} />
            <BookingFiltersModal />
            <CreateBookingExternalModal />
            <LogisticPlanModal />
          </>
        )}
      </div>
      {layout === BookingLayout.Calendar && <BookingPrintZones />}
    </div>
  );
};

export default Bookings;
